// Hülle einer Programm-Website: Kopf mit Seitentitel, Platz für seitenbezogene Reiter und Aktionen, Inhalt.
// Im Rahmen (Rahmen-Website lädt das Programm im Inhaltsbereich) ohne eigene Seitenleiste – der Rahmen zeigt sie und
// bekommt Ort und Titel gemeldet. Allein aufgerufen bringt das Programm seine eigene Seitenleiste mit.
import { Menu, Search } from "lucide-react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation, useNavigate } from "react-router";
import { Banner } from "../bausteine/einfach";
import { PROGRAMMNAME } from "../kern/marke";
import { BEREICH_TITEL, useBereichModi } from "../kern/modus";
import { useProgramm } from "../kern/programm";
import { anRahmen, imRahmen, istNachricht } from "../kern/rahmen";
import { useSitzung } from "../kern/sitzung";
import { BEREICHE, PROGRAMME, SEITEN, type Bereich } from "../navigation/katalog";
import { Befehlspalette } from "./Befehlspalette";
import { Seitenleiste } from "./Seitenleiste";
import { useEingeklappt } from "./seitenleisteLogik";

const KopfZiel = createContext<HTMLElement | null>(null);
const KopfMitteZiel = createContext<HTMLElement | null>(null);
const KopfTitel = createContext<(titel: string | null) => void>(() => {});

/** Detailseiten setzen ihren Titel im Kopf (z. B. „Objekt 019"). */
// eslint-disable-next-line react-refresh/only-export-components
export function useKopfTitel(titel: string | null) {
  const setzen = useContext(KopfTitel);
  useEffect(() => {
    setzen(titel);
    return () => setzen(null);
  }, [titel, setzen]);
}

/** Aktionen einer Seite erscheinen rechts im Kopf (höchstens ein Hauptknopf je Ansicht). */
export function KopfAktionen({ children }: { children: ReactNode }) {
  const ziel = useContext(KopfZiel);
  return ziel ? createPortal(children, ziel) : null;
}

/** Reiter und Auswahl einer Seite direkt neben dem Titel (z. B. Dokumente | Zähler | Fotos). */
export function KopfMitte({ children }: { children: ReactNode }) {
  const ziel = useContext(KopfMitteZiel);
  return ziel ? createPortal(children, ziel) : null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function bereichZuPfad(pfad: string): Bereich | undefined {
  if (pfad === "/") return BEREICHE[0];
  const seite = SEITEN.find((s) => s.pfad === pfad);
  if (seite) return BEREICHE.find((b) => b.id === seite.bereich);
  return BEREICHE.filter((b) => b.pfad !== "/" && (pfad === b.pfad || pfad.startsWith(`${b.pfad}/`)))
    .sort((a, b) => b.pfad.length - a.pfad.length)[0];
}

/** Welche gespiegelten Bereiche (bereich_modus) eine Seite betrifft – je Bereich der Seite, nicht je Programm: Das Band
 *  steht nur dort, wo es Schreib-Routen mit Spiegel-Sperre gibt (Stammdaten, Team-Daten, Umsätze). Rechnungen, DATEV,
 *  Posteingang und Werkzeuge schreiben über erlaubte Wege direkt ins Altsystem – dort kein Band. */
const SPIEGEL_JE_SEITENBEREICH: Record<string, string[]> = {
  bestand: ["stammdaten"], vermietung: ["stammdaten"], darlehen: ["stammdaten"], team: ["team"], finanzen: ["umsaetze"],
  daten: ["stammdaten", "team", "umsaetze"],
};
/** Bereiche ohne erlaubten Schreibweg: hier nur lesen. In den anderen geht ein Teil direkt ins Altsystem. */
const NUR_LESEN = new Set(["team"]);

function spiegelText(bereiche: string[], seitenbereich: string | undefined): string {
  const namen = bereiche.map((b) => BEREICH_TITEL[b] ?? b);
  const wer = namen.length > 1 ? `${namen.slice(0, -1).join(", ")} und ${namen.at(-1)} führen` : `${namen[0]} führt`;
  if (seitenbereich === "daten") {
    return `${wer} noch das bisherige IMHO (laufend gespiegelt) – deren Tabellen hier nur lesen; Ändern wird mit der `
      + "Umschaltung freigeschaltet, bis dahin im bisherigen IMHO.";
  }
  if (bereiche.every((b) => NUR_LESEN.has(b))) {
    return `${wer} noch das bisherige IMHO (laufend gespiegelt) – hier nur lesen. Erfassen und Ändern wird mit der `
      + "Umschaltung freigeschaltet, bis dahin im bisherigen IMHO.";
  }
  return `${wer} noch das bisherige IMHO (laufend gespiegelt). Was dort schon geht, wird direkt dort gespeichert; `
    + "ausgegraute Knöpfe werden mit der Umschaltung freigeschaltet – bis dahin im bisherigen IMHO.";
}

export function AppShell() {
  const { abmelden } = useSitzung();
  const ort = useLocation();
  const navigate = useNavigate();
  const programm = useProgramm();
  const eingebettet = imRahmen();
  const [palette, setPalette] = useState(false);
  const [menue, setMenue] = useState(false);
  const [eingeklappt, setEingeklappt] = useEingeklappt();
  const [kopfZiel, setKopfZiel] = useState<HTMLElement | null>(null);
  const [kopfMitte, setKopfMitte] = useState<HTMLElement | null>(null);
  const [titelDetail, setTitelDetail] = useState<string | null>(null);

  useEffect(() => setMenue(false), [ort.pathname]);

  const seite = SEITEN.find((s) => s.pfad === ort.pathname);
  const bereich = bereichZuPfad(ort.pathname);
  const titel = titelDetail ?? seite?.titel ?? bereich?.titel ?? PROGRAMMNAME;
  const programmTitel = PROGRAMME.find((p) => p.id === programm)?.titel;

  useEffect(() => {
    document.title = `${titel} · ${PROGRAMMNAME}`;
    if (eingebettet) anRahmen({ typ: "imho:ort", pfad: `${ort.pathname}${ort.search}`, titel });
  }, [titel, eingebettet, ort.pathname, ort.search]);

  // Im Rahmen: Navigation des Rahmens annehmen, ⌘K an den Rahmen weitergeben
  useEffect(() => {
    if (!eingebettet) return;
    const nachricht = (e: MessageEvent) => {
      if (istNachricht(e) && e.data.typ === "imho:gehe") navigate(e.data.pfad, { replace: true });
    };
    const taste = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); anRahmen({ typ: "imho:palette" }); }
    };
    window.addEventListener("message", nachricht);
    window.addEventListener("keydown", taste);
    return () => { window.removeEventListener("message", nachricht); window.removeEventListener("keydown", taste); };
  }, [eingebettet, navigate]);

  const modi = useBereichModi();
  // nur dort, wo wirklich gesperrt ist: Schreib-Routen mit Spiegel-Sperre gibt es für Stammdaten, Team und Umsätze
  const betroffen = SPIEGEL_JE_SEITENBEREICH[bereich?.id ?? ""] ?? [];
  const gesperrt = (modi ?? []).filter((m) => m.modus !== "eigen" && betroffen.includes(m.bereich)).map((m) => m.bereich);
  const gespiegelt = gesperrt.length > 0;

  return (
    <div className="im-root im-shell" data-menue={menue ? "offen" : undefined} data-eingeklappt={eingeklappt && !eingebettet ? "ja" : undefined}
      data-eingebettet={eingebettet ? "ja" : undefined}>
      {!eingebettet && (
        <Seitenleiste programm={programm} pfad={ort.pathname} gehe={(p) => navigate(p)} mitProgrammwahl={false}
          eingeklappt={eingeklappt} setEingeklappt={setEingeklappt}
          abmelden={() => { void abmelden().then(() => navigate("/anmelden")); }} />
      )}
      {menue && <div className="im-nav-schleier" aria-hidden="true" onClick={() => setMenue(false)} />}
      <div className="im-main">
        <header className="im-header">
          <button type="button" className="im-btn im-btn--ghost im-btn--sm im-nur-mobil" aria-label="Menü"
            aria-expanded={menue} onClick={() => (eingebettet ? anRahmen({ typ: "imho:menue" }) : setMenue(!menue))}>
            <Menu strokeWidth={1.5} aria-hidden="true" />
          </button>
          {programm !== "start" && <span className="im-small im-header__programm">{programmTitel}</span>}
          <h1 className="im-header__title">{titel}</h1>
          <div className="im-header__mitte" ref={setKopfMitte} />
          <div className="im-header__aktionen" ref={setKopfZiel} />
          <button type="button" className="im-btn im-btn--ghost im-suchknopf"
            onClick={() => (eingebettet ? anRahmen({ typ: "imho:palette" }) : setPalette(true))}>
            <Search strokeWidth={1.5} aria-hidden="true" /><span>Suchen</span><span className="im-kbd">⌘K</span>
          </button>
        </header>
        {gespiegelt && (
          <Banner art="spiegel" marke="Spiegel">{spiegelText(gesperrt, bereich?.id)}</Banner>
        )}
        <main className={seite?.vollflaeche ? "im-content im-content--voll" : "im-content"} id="inhalt">
          <KopfZiel.Provider value={kopfZiel}>
            <KopfMitteZiel.Provider value={kopfMitte}>
              <KopfTitel.Provider value={setTitelDetail}>
                <Outlet />
              </KopfTitel.Provider>
            </KopfMitteZiel.Provider>
          </KopfZiel.Provider>
        </main>
      </div>
      {!eingebettet && <Befehlspalette offen={palette} setOffen={setPalette} gehe={(p) => navigate(p)} />}
    </div>
  );
}
